import Vue from 'vue';
import * as auth from './utils/auth.js';
import store from './store';
import createRouter from './router/index.js';
import createApi from './api/index.js';
import createPermission from './permission';
import { whiteList as permissionWhiteList } from './permission';
import registerFilters from './utils/filters';
import registerComponents from './utils/components';
import registerDirectives from './utils/directives';
import registerPlugins from './plugins/index';
import ability from './plugins/ability';
import App from './App.vue';
import VueI18n from 'vue-i18n';
import messages from './../i18n/index';
import config from './../config/index';
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import(`./assets/scss/${process.env.PLATFORM}.scss`);
// eslint-disable-next-line
console.info(process.env.APP_VER);

Vue.prototype.$config = config;

if (
  process.env.SENTRY_URL &&
  process.env.SENTRY_URL !== '' &&
  process.env.NODE_ENV === 'production'
) {
  Sentry.init({
    dsn: process.env.SENTRY_URL,
    release: `cryptotraffic@${process.env.APP_VER}`,
    // beforeSend(event) {
    //   // Check if it is an exception, and if so, show the report dialog
    //   if (event.exception) {
    //     Sentry.showReportDialog({ eventId: event.event_id });
    //   }
    //   return event;
    // },
    integrations: [
      new Integrations.Vue({
        Vue,
        attachProps: true
      })
    ]
  });
}

registerPlugins();
registerFilters();
registerComponents();
registerDirectives();

createApi(
  [
    {
      name: 'auth',
      baseUrl: `${process.env.AUTH_API_HOST}`,
      authRequired: true,
      lang: true
    },
    {
      name: 'bounty',
      baseUrl: `${process.env.BOUNTY_API_HOST}`,
      authRequired: true
    },
    {
      name: 'adnet',
      baseUrl: `${process.env.ADNET_API_HOST}`,
      authRequired: true
    },
    {
      name: 'core',
      baseUrl: `${process.env.CORE_API_HOST}`
    }
  ],
  {
    token: auth.readToken(),
    refreshToken: auth.readRefreshToken()
  }
);

// Ugly hack for update all components when role has changed.
Vue.mixin({
  computed: {
    _currentRole() {
      return store.getters.role;
    }
  },

  watch: {
    _currentRole(role) {
      this.$ability.update(ability.getRoleAbilities(role));
      this.$forceUpdate();
    }
  }
});

const app = new Vue({
  el: '#app',
  i18n: new VueI18n({
    locale: process.env.LANGUAGE,
    messages,
    pluralizationRules: {
      ru: function(choice, choicesLength) {
        if (choice === 0) return 0;

        const teen = choice > 10 && choice < 20;
        const endsWithOne = choice % 10 === 1;

        if (choicesLength < 4) return !teen && endsWithOne ? 0 : 1;
        if (!teen && endsWithOne) return 1;
        if (!teen && choice % 10 >= 2 && choice % 10 <= 4) return 2;
        return choicesLength < 4 ? 2 : 3;
      }
    }
  }),
  store,
  router: createRouter(),

  data() {
    return {
      screen: {
        xs: false,
        sm: false,
        md: true,
        lg: false,
        xl: false,
        other: false
      },
      windowWidth: window.innerWidth
    };
  },

  methods: {
    getScreen() {
      const SM = 576;
      const MD = 768;
      const LG = 992;
      const XL = 1368;

      const RATIO = 3;
      const { body } = document;
      let rect = body.getBoundingClientRect();
      let screen = 'other';
      if (rect.width - RATIO < SM) {
        screen = 'xs';
      } else if (rect.width - RATIO < MD && rect.width - RATIO > SM) {
        screen = 'sm';
      } else if (rect.width - RATIO < LG && rect.width - RATIO > MD) {
        screen = 'md';
      } else if (rect.width - RATIO < XL && rect.width - RATIO > LG) {
        screen = 'lg';
      } else if (rect.width - RATIO > XL) {
        screen = 'xl';
      }
      return screen;
    },
    toggleScreen(device) {
      this.$root.screen.xs = device === 'xs';
      this.$root.screen.sm = device === 'sm';
      this.$root.screen.md = device === 'md';
      this.$root.screen.lg = device === 'lg';
      this.$root.screen.xl = device === 'xl';
      this.$root.screen.other = device === 'other';
    },
    setWindowWidth(width) {
      this.$root.windowWidth = width;
    },
    resizeHandler() {
      if (document.hidden) return;
      this.toggleScreen(this.getScreen());
      this.setWindowWidth(window.innerWidth);
    }
  },

  async created() {
    if (
      !auth.readToken() &&
      permissionWhiteList.indexOf(window.location.pathname) === -1
    ) {
      this.$store.commit('SET_ENTRY_URL', window.location.pathname);
      this.$router.replace({
        name: 'login'
      });
    }

    if (auth.readToken()) {
      await this.$store.dispatch('fetchUser');
    }

    if (!auth.readActiveRole() && auth.readToken()) {
      const adminIndex = this.$store.state.app.user.roles.indexOf(
        this.$config.rolesIds['admin']
      );

      this.$store.dispatch(
        'switchRole',
        this.$store.state.app.user.roles[adminIndex] ||
          this.$store.state.app.user.roles[0]
      );
    } else {
      this.$store.dispatch('switchRole', auth.readActiveRole());
    }

    createPermission(this);
  },

  mounted() {
    this.resizeHandler();
    document.addEventListener('visibilitychange', this.resizeHandler);
    window.addEventListener('DOMContentLoaded', this.resizeHandler);
    window.addEventListener('resize', this.resizeHandler);
  },

  render: h => h(App)
});
export { store };
export default app;
